/**
 * ComplianceStatusPanel — account-level compliance status for the generative canvas (TASK-555).
 * Self-fetches the honest summary. NEVER shows a fake-green pass: a null score / no data renders
 * a gray "No data" state, and open reviews/alerts drive an amber/red status.
 */
import React from 'react';
import { useComplianceStatus } from '../../hooks/useComplianceStatus';
import { deriveComplianceOverall } from '../../utils/complianceStatus';

const ComplianceStatusPanel: React.FC = () => {
  const { data, isLoading, isError } = useComplianceStatus();

  // M1: score-thresholded via the shared helper — a low score never renders green "Compliant".
  const overall = deriveComplianceOverall(data, { loading: isLoading, error: isError });
  const OverallIcon = overall.Icon;

  const stat = (v: number | null | undefined, suffix = '') =>
    isLoading ? '…' : v === null || v === undefined ? '—' : `${v}${suffix}`;

  return (
    <div className="p-1">
      <h3 className="mb-3 text-sm font-semibold text-foreground">Compliance Status</h3>
      <div className="mb-3 flex items-center gap-2">
        <OverallIcon className={`h-6 w-6 ${overall.text}`} />
        <span className={`text-xl font-bold ${overall.text}`}>{overall.label}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg border border-border p-2">
          <p className="text-xs text-muted-foreground">Pending</p>
          <p className="text-lg font-bold text-foreground">{stat(data?.pending_reviews)}</p>
        </div>
        <div className="rounded-lg border border-border p-2">
          <p className="text-xs text-muted-foreground">Risk Alerts</p>
          <p className="text-lg font-bold text-foreground">{stat(data?.risk_alerts)}</p>
        </div>
        <div className="rounded-lg border border-border p-2">
          <p className="text-xs text-muted-foreground">Score</p>
          <p className="text-lg font-bold text-foreground">{stat(data?.compliance_score, '%')}</p>
        </div>
      </div>
    </div>
  );
};

export default ComplianceStatusPanel;
